import {
    Button,
    Modal,
    InputDeprecated,
    InfoCard,
    InlineAlert,
} from '@/components';
import { useForm } from 'react-hook-form';
import { R } from '@/features/programs/resources/ProgramsResources.resx';

import * as React from 'react';
import { format as formatString } from '@/Resources.resx';
import { isWeightTooLowBMI } from '@/shared/helpers/isWeightTooLowBMI';
import { calculateMinimalWeight } from '@/shared/helpers/calculateMinimalWeightBMI';
import { formatNumber } from '@/shared/helpers/formatNumber';
import { useAtom } from 'jotai/index';
import { updateSundVaegtProgramMutationAtom } from '../model/updateSundVaegtProgramMutationAtom';

export type SetWeightGoalModalProps = {
    height: number;
    initialWeight: number;
    currentWeightGoal: number;
    onClose: () => void;
};

export type IFormSetWeightGoal = { weightGoal: number };

export const SetWeightGoalModal = (props: SetWeightGoalModalProps) => {
    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
    } = useForm<IFormSetWeightGoal>({
        mode: 'onSubmit',
        defaultValues: { weightGoal: props.currentWeightGoal },
    });

    const weightGoal = watch('weightGoal');

    // We only consider data "complete" if BOTH height and initial weight are present and > 0
    const hasAnthropometricData = props.height > 0 && props.initialWeight > 0;

    // BMI-derived minimal weight only meaningful with valid height & initial weight
    const bmiMinimalWeight = hasAnthropometricData
        ? calculateMinimalWeight(props.height)
        : undefined;

    // Effective minimal weight used for validation (BMI-based or fallback = 50 kg)
    const effectiveMinimalWeight = hasAnthropometricData
        ? bmiMinimalWeight!
        : 50;

    const [{ mutate, status, reset, error }] = useAtom(
        updateSundVaegtProgramMutationAtom,
    );

    const [showErrors, setShowErrors] = React.useState(false);

    // Show InfoCard only when we have full anthropometric data and the entered goal is below BMI threshold
    const showInfo =
        hasAnthropometricData &&
        !['', null, undefined].includes(weightGoal?.toString()) &&
        !isNaN(Number(weightGoal)) &&
        isWeightTooLowBMI(Number(weightGoal), props.height);

    const onSubmit = (data: IFormSetWeightGoal) => {
        mutate(
            { DesiredWeightKg: data.weightGoal },
            {
                onSuccess: () => {
                    props.onClose();
                },
                onError: () => {
                    reset();
                },
            },
        );
    };

    const onError = () => {
        setShowErrors(true);
    };

    return (
        <Modal
            onClose={() => {
                reset();
                props.onClose();
            }}
            title={R.SundvaegtSetWeightGoalModalTitle}
        >
            {status !== 'error' && (
                <>
                    <p className="text-size-[18px] text-gray-900">
                        {R.SundvaegtSetWeightGoalModalDescription}
                    </p>
                    <form onSubmit={handleSubmit(onSubmit, onError)}>
                        <InputDeprecated
                            type="number"
                            suffix="kg"
                            placeholder={
                                R.SundvaegtSetWeightGoalInputPlaceholder
                            }
                            label={R.SundvaegtSetWeightGoalModalInputLabel}
                            inputClassName={`${
                                showErrors && errors.weightGoal
                                    ? 'border border-secondary-red'
                                    : ''
                            }`}
                            containerClassName="mt-8"
                            {...register('weightGoal', {
                                required:
                                    R.SundvaegtSetWeightGoalModalInputRequiredLabel,
                                validate: {
                                    weightGoalNotMoreThenInitialWeight: (
                                        value: number,
                                    ) => {
                                        // Enforce if we have any valid initialWeight (height not required here)
                                        if (
                                            props.initialWeight > 0 &&
                                            value > props.initialWeight
                                        ) {
                                            return R.SundvaegtSetWeightGoalModalValidationWeightGoalTooHigh;
                                        }
                                        return true;
                                    },
                                    weightGoalTooLow: (value: number) => {
                                        if (
                                            value !== null &&
                                            value !== undefined &&
                                            !isNaN(Number(value)) &&
                                            Number(value) <
                                                effectiveMinimalWeight
                                        ) {
                                            return formatString(
                                                R.SundvaegtSetWeightGoalModalValidationWeightGoalTooLow,
                                                formatNumber(
                                                    effectiveMinimalWeight,
                                                ),
                                            ).toString();
                                        }
                                        return true;
                                    },
                                },
                                onChange: () => setShowErrors(false),
                            })}
                            error={
                                showErrors
                                    ? errors.weightGoal?.message
                                    : undefined
                            }
                        />
                        {/* InfoCard only when both height and initial weight are present */}
                        {showInfo && showErrors && (
                            <InfoCard className="mt-4">
                                <h3 className="text-primary-blue font-size-[18px] font-bold">
                                    {R.SundvaegtSetWeightGoalModalInfoTitle}
                                </h3>
                                <p className="font-size-[18px] text-gray-900">
                                    {formatString(
                                        R.SundvaegtSetWeightGoalModalInfoDescription,
                                        formatNumber(bmiMinimalWeight!),
                                    ).toString()}
                                </p>
                            </InfoCard>
                        )}
                        <div className="mt-8 flex w-full flex-row justify-end">
                            <Button variant="primary" type="submit">
                                {R.SundvaegtSetWeightGoalModalSubmitButton}
                            </Button>
                        </div>
                    </form>
                </>
            )}
            {status === 'error' && (
                <InlineAlert onClose={reset} errorMessage={error?.message} />
            )}
        </Modal>
    );
};
